import { useErrorReporter } from '@/composables/useErrorReporter'
import {
	API_BASE, API_ENDPOINT_PORTAL_ACCOUNTS_TAGS, API_ENDPOINT_PORTAL_ACCOUNTS_TAGS_APPLY
} from '@/constants/index'
import type {
	MappedTag, TableTag, TagApiResponse, UpdateAction
} from '@/types/tags'
import { mapTableTags, mapTags } from '@/utils/mappers/TagMap'
import { ref } from 'vue'

const tags = ref<MappedTag[]>([])
const tableTags = ref<TableTag[]>([])
const loading = ref(false)

export function useTags() {
	const { bugsnagReport } = useErrorReporter()
	const getTags = async () => {
		loading.value = true

		await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_ACCOUNTS_TAGS}`, {
			method: 'GET',
			credentials: 'include',
			onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error fetching tags')
				}
				tags.value = mapTags(response._data as TagApiResponse[])
				// Sort tags by name
				tags.value.sort((a, b) => {
					return a.name.localeCompare(b.name)
				})
				tableTags.value = mapTableTags(response._data as TagApiResponse[])
				// Sort table tags by name
				tableTags.value.sort((a, b) => {
					return a.name.localeCompare(b.name)
				})
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error fetching tags',
					message: e.message || ''
				}
			})
		}).finally(() => {
			loading.value = false
		})
	}

	const addTag = async (tagName: string, accountIds?: number[]) => {
		loading.value = true
		await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_ACCOUNTS_TAGS}`, {
			method: 'POST',
			credentials: 'include',
			body: {
				tagName,
				accountIds
			},
			onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error adding tag')
				}
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error adding tag',
					message: e.message || ''
				}
			})
		}).finally(async () => {
			await getTags()
			loading.value = false
		})
	}

	const updateTag = async (tagId: string, accountIds: number[], action: UpdateAction) => {
		loading.value = true

		await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_ACCOUNTS_TAGS_APPLY}`, {
			method: 'PUT',
			credentials: 'include',
			body: {
				tagId: parseInt(tagId),
				accountIds,
				action
			},
			onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error updating tag')
				}
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error updating tag',
					message: e.message || ''
				}
			})
		}).finally(() => {
			loading.value = false
		})
	}

	return {
		tags,
		tableTags,
		loading,
		getTags,
		addTag,
		updateTag
	}
}
